import { Error as ErrorIcon } from '@mui/icons-material';
import {
  Alert,
  Box,
  Collapse,
  Grid,
  InputAdornment,
  TextField,
  Typography,
} from '@mui/material';
import { SubProduct } from 'lib/api/creditLine/types';
import React, { useMemo } from 'react';

interface RateControls {
  variableRate?: number;
  fixedRate?: number;
  variableDiscount?: number;
  fixedDiscount?: number;
}

interface RatesCollapseProps {
  show: boolean;
  rateControls: RateControls;
  onRateChange: (field: keyof RateControls, value: number) => void;
  subProductsData?: SubProduct[];
  creditLineStatus?: string;
  isLoadingCreditLine?: boolean;
  type: string;
}

const RatesCollapse: React.FC<RatesCollapseProps> = ({
  show,
  rateControls,
  onRateChange,
  subProductsData,
  creditLineStatus,
  isLoadingCreditLine = false,
  type,
}) => {
  const shouldShowMissingCreditLineAlert = useMemo(() => {
    if (isLoadingCreditLine || !type) return false;
    if (!creditLineStatus && !subProductsData?.length) return false;

    const hasActiveCreditLine = creditLineStatus === 'ACTIVE';
    const hasMatchingSubProduct = subProductsData?.some(
      (sp) => sp.subProductId === type,
    );

    return !hasActiveCreditLine || !hasMatchingSubProduct;
  }, [creditLineStatus, isLoadingCreditLine, subProductsData, type]);

  return (
    <>
      <Collapse in={show}>
        <Box
          sx={{
            mb: 2,
            p: 1.5,
            backgroundColor: 'rgba(25, 118, 210, 0.04)',
            borderRadius: 1,
          }}
        >
          <Typography
            variant="subtitle2"
            sx={{ mb: 1.5, fontWeight: 600, color: 'primary.main' }}
          >
            Configuración de Tasas
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Tasa Variable (%)"
                type="number"
                value={rateControls.variableRate || ''}
                onChange={(e) =>
                  onRateChange(
                    'variableRate',
                    e.target.value === '' ? 0 : parseFloat(e.target.value) || 0,
                  )
                }
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">%</InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Descuento Variable (%)"
                type="number"
                value={rateControls.variableDiscount || ''}
                onChange={(e) =>
                  onRateChange(
                    'variableDiscount',
                    e.target.value === '' ? 0 : parseFloat(e.target.value) || 0,
                  )
                }
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">%</InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Tasa Fija (UF)"
                type="number"
                value={rateControls.fixedRate ?? ''}
                onChange={(e) =>
                  onRateChange(
                    'fixedRate',
                    e.target.value === '' ? 0 : parseFloat(e.target.value) || 0,
                  )
                }
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">UF</InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Descuento Fijo (%)"
                type="number"
                value={rateControls.fixedDiscount || ''}
                onChange={(e) =>
                  onRateChange(
                    'fixedDiscount',
                    e.target.value === '' ? 0 : parseFloat(e.target.value) || 0,
                  )
                }
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">%</InputAdornment>
                  ),
                }}
              />
            </Grid>
          </Grid>
        </Box>
      </Collapse>
      {shouldShowMissingCreditLineAlert && (
        <Alert
          severity="error"
          icon={false}
          sx={{
            borderRadius: 0.5,
            backgroundColor: 'rgba(211, 47, 47, 0.08)',
            border: '1px solid rgba(211, 47, 47, 0.2)',
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              width: '100%',
              justifyContent: 'center',
            }}
          >
            <ErrorIcon
              sx={{ fontSize: '1.2rem', color: 'error.main', mr: 1 }}
            />
            <Typography
              variant="body2"
              sx={{ fontSize: '0.75rem', display: 'inline-block' }}
            >
              El cliente no tiene línea de crédito activa para el tipo de
              garantía
            </Typography>
          </Box>
        </Alert>
      )}
    </>
  );
};

export default RatesCollapse;
